/**
 * SettingsFavicon — Iter 191a.
 *
 * Squarespace-style favicon picker, rendered in the app's dark-violet theme.
 * Upload a "Default" favicon (light-mode / everyday) and an optional
 * "Dark mode" favicon that browsers will pick up via `prefers-color-scheme`.
 *
 * Persistence:
 *   GET  /api/builder/settings                              (returns `favicon`)
 *   PUT  /api/builder/settings { favicon, favicon_dark }    (both fields)
 *
 * The backend already handles `favicon` (see `content_routes.py:88/109`);
 * `favicon_dark` is a new optional field the same handler passes through.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Loader2, Search, Trash2 } from 'lucide-react';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function FaviconSlot({ label, value, onChange, testId, maxKb = 100, hintSize = '0.1 MB max', ctaLabel = 'Add a favicon' }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem('token');

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > maxKb * 1024) {
      toast.error(`File must be under ${hintSize}`);
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/media/upload?category=branding`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onChange(process.env.REACT_APP_BACKEND_URL + res.data.url);
      toast.success(`${label} uploaded`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`favicon-slot-${testId}`}>
      <div className="text-[10.5px] uppercase tracking-[0.20em] text-[#64748B] font-bold">
        {label}
      </div>
      <div
        onClick={() => !value && fileRef.current?.click()}
        className={`relative rounded-xl border-2 border-dashed transition-all overflow-hidden group ${
          value
            ? 'border-white/[0.08] bg-white/[0.03]'
            : 'border-[#A78BFA]/25 hover:border-[#A78BFA]/60 bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer'
        }`}
        style={{ minHeight: 220 }}
      >
        {value ? (
          <div className="flex flex-col items-center justify-center h-full py-10 gap-3">
            <img
              src={value}
              alt={label}
              className="w-20 h-20 rounded-lg object-contain bg-white/5 border border-white/10 p-2"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                className="px-3 py-1.5 rounded-md text-[11px] font-semibold text-white bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 transition-all"
                data-testid={`favicon-replace-${testId}`}
              >
                Replace
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange(''); toast.success(`${label} favicon removed`); }}
                className="px-3 py-1.5 rounded-md text-[11px] font-semibold text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 transition-all inline-flex items-center gap-1.5"
                data-testid={`favicon-remove-${testId}`}
              >
                <Trash2 size={11} /> Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full py-14 gap-3 text-white/60 group-hover:text-white transition-colors">
            <div className="w-12 h-12 rounded-full bg-white/[0.06] border border-white/10 grid place-items-center group-hover:bg-[#A78BFA]/20 group-hover:border-[#A78BFA]/40 transition-all">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={18} />}
            </div>
            <div className="text-center">
              <div className="text-[13px] font-semibold text-white">
                {uploading ? 'Uploading…' : ctaLabel}
              </div>
              <div className="text-[10.5px] text-[#64748B] mt-1">{hintSize}</div>
            </div>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/x-icon"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files?.[0])}
          data-testid={`favicon-input-${testId}`}
        />
      </div>
    </div>
  );
}

export default function SettingsFavicon() {
  const [logo, setLogo] = useState('');
  const [logoDark, setLogoDark] = useState('');
  const [favicon, setFavicon] = useState('');
  const [faviconDark, setFaviconDark] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/builder/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLogo(res.data?.logo || '');
        setLogoDark(res.data?.logo_dark || '');
        setFavicon(res.data?.favicon || '');
        setFaviconDark(res.data?.favicon_dark || '');
      } catch (_) {
        // Non-fatal; user can still upload
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const persist = async (patch) => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/settings`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-full" data-testid="settings-favicon-page">
      {/* Squarespace-style top rail — matches the reference screenshot but in
          our dark-violet theme. */}
      <div className="border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-xl px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackToSettings />
          <div className="w-px h-4 bg-white/10" />
          <h1 className="text-[13px] font-bold text-white uppercase tracking-[0.18em]">Settings</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="w-8 h-8 grid place-items-center text-white/60 hover:text-white hover:bg-white/[0.06] rounded-md transition-all"
            title="Search settings"
            data-testid="settings-search-btn"
          >
            <Search size={14} />
          </button>
          {saving && (
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#A78BFA] font-bold animate-pulse">
              Saving…
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-8 py-10">
        {/* Breadcrumb */}
        <div className="text-[12px] text-[#64748B] mb-3">
          <span className="text-white/70 font-semibold">Website</span>
          <span className="mx-2">/</span>
          <span className="text-white">Logo &amp; Favicon</span>
        </div>

        <h2 className="text-[32px] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>
          Logo &amp; Favicon
        </h2>
        <p className="mt-3 text-[14px] text-[#9CA3AF] leading-relaxed max-w-2xl">
          Upload the logo that appears in your storefront navigation and the tiny favicon shown in the
          browser tab. Add optional dark-mode variants and we&rsquo;ll pick the right one automatically.
        </p>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-white/50">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Logo section */}
            <div className="mt-10">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-[14px] font-bold text-white uppercase tracking-[0.16em]">Logo</h3>
                <span className="text-[11px] text-[#64748B]">Shown in your site navigation</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <FaviconSlot
                  label="Default logo"
                  value={logo}
                  onChange={(url) => { setLogo(url); persist({ logo: url }); }}
                  testId="logo-default"
                  maxKb={2048}
                  hintSize="2 MB max"
                  ctaLabel="Add a logo"
                />
                <FaviconSlot
                  label="Dark-mode logo (optional)"
                  value={logoDark}
                  onChange={(url) => { setLogoDark(url); persist({ logo_dark: url }); }}
                  testId="logo-dark"
                  maxKb={2048}
                  hintSize="2 MB max"
                  ctaLabel="Add a logo"
                />
              </div>
              <p className="mt-4 text-[12px] text-[#64748B] leading-relaxed">
                Recommended: <span className="text-white/85 font-semibold">PNG or SVG</span> at{' '}
                <span className="text-white/85 font-semibold">200 × 60 px</span> (horizontal) or{' '}
                <span className="text-white/85 font-semibold">400 × 400 px</span> (square). Transparent
                backgrounds look best.
              </p>
            </div>

            {/* Favicon section */}
            <div className="mt-12">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-[14px] font-bold text-white uppercase tracking-[0.16em]">Favicon</h3>
                <span className="text-[11px] text-[#64748B]">Shown in the browser tab</span>
              </div>
              <div className="space-y-6">
                <FaviconSlot
                  label="Default"
                  value={favicon}
                  onChange={(url) => { setFavicon(url); persist({ favicon: url }); }}
                  testId="default"
                />
                <FaviconSlot
                  label="Dark mode (optional)"
                  value={faviconDark}
                  onChange={(url) => { setFaviconDark(url); persist({ favicon_dark: url }); }}
                  testId="dark"
                />
              </div>
              <p className="mt-6 text-[12px] text-[#64748B] leading-relaxed">
                Icons should be in <span className="text-white/85 font-semibold">PNG</span> file format and recommended sizing is between{' '}
                <span className="text-white/85 font-semibold">100 × 100 px</span> and{' '}
                <span className="text-white/85 font-semibold">300 × 300 px</span>.{' '}
                <a
                  href="https://support.squarespace.com/hc/en-us/articles/205813588-Adding-a-favicon"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#A78BFA] hover:text-white underline underline-offset-2"
                >
                  Learn more
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
